import { useQuery } from '@apollo/client';
import {
  ActionGroup,
  PermissionToggle,
  Loader,
  ALL_SHARD_MODULES,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  getParsedCrudData
} from '@nile/components';
import {
  Heading,
  Page,
  TextStyle,
  ResourceList,
  ResourceItem,
  InlineEdit,
  DisplayText
} from '@superops/ui-design-system';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountClient, GET_FIELD } from '@nile/graphql';
import { SVGIcon } from '@superops/super-icons';
import { isEmpty, map, orderBy, pick } from 'lodash';
import Picker from './Picker';
import { useFlags } from 'launchdarkly-react-client-sdk';

const SVGMap = {
  0: 'UpArrow',
  1: 'UpArrow',
  2: 'UpArrow',
  3: 'DownArrow',
  4: 'DownArrow'
};

const PriorityWrapper = styled.div`
  .heading {
    font-weight: 600;
    font-size: 20px;
    line-height: 21px;
    margin-bottom: 10px;
  }
  .description {
    font-size: 18px;
    line-height: 27px;
    margin-bottom: 20px;
  }

  .priority-wrapper {
    max-width: 400px;
  }
`;

const PriorityItem = styled.div`
  &.isEdit {
    padding: 6px 20px 12px;
  }
  &:hover {
    .pencil {
      visibility: visible;
    }
  }
`;

const Priority = (props) => {
  const { index } = props;
  const [edit, setEdit] = useState(false);
  const { item } = props;
  const id = item?.id;
  const value = item?.value;
  const colorCode = item?.details?.colorCode;

  const handleFocusOut = (updatedText) => {
    props.handleFocusOut(item, updatedText);
    setEdit(false);
  };

  if (edit) {
    return (
      <PriorityItem
        className="fx actr isEdit g5"
        superid={`UrgencyPriorityEdit-${id}`}
      >
        <div className="flex">
          <SVGIcon name={SVGMap?.[index]} color={colorCode} />
        </div>
        <div className="fx actr priority-list">
          <Heading element="h3">
            <InlineEdit
              text={value}
              labelClassName="InlineEditLabel"
              inputClassName="InlineEditInput"
              inputWidth="180px"
              inputHeight="25px"
              onFocus={() => {}}
              isEditing
              onFocusOut={handleFocusOut}
            />
          </Heading>
        </div>
      </PriorityItem>
    );
  }

  return (
    <ResourceItem
      id={`priority${id}`}
      accessibilityLabel={`View details for ${value}`}
    >
      <PriorityItem className="fx actr hiddenContainer">
        <div className="flex">
          <SVGIcon name={SVGMap?.[index]} color={colorCode} />
        </div>
        <div className="fx actr priority-list">
          <DisplayText element="p" size="large" weight="Medium">
            <div className="flex actr">
              <div className="mr8">{value}</div>
              {props.isEditAuthorised && (
                <div
                  className="hiddenFxActions"
                  onClick={(e) => {
                    setEdit(true);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <SVGIcon name="Edit" />
                </div>
              )}
            </div>
          </DisplayText>
        </div>
      </PriorityItem>
    </ResourceItem>
  );
};

function PriorityPage(props) {
  const { isEditAuthorised } = props;
  const [priorityData, setPriorityData] = useState([]);
  const flags = useFlags();

  const [mandatoryOnCreation, setMandatoryOnCreation] = useState(false);
  const [mandatoryOnClosure, setMandatoryOnClosure] = useState(false);

  const { data, loading, error, refetch: refetchResolution } = useQuery(
    GET_FIELD,
    {
      client: AccountClient,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          entity: ALL_SHARD_MODULES.ticket,
          columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.PRIORITY
        }
      },
      onCompleted({ getField }) {
        if (getField?.options || getField?.fieldOptions) {
          setPriorityData(
            orderBy(getField?.options, 'order') || getField?.fieldOptions
          );
        }
        setMandatoryOnCreation(getField?.mandatoryOnCreate);
        setMandatoryOnClosure(getField?.mandatoryOnClosure);
      }
    }
  );

  const updatePriority = (item, text) => {
    const base = [...priorityData];
    let updatedPriorities = [];
    base.forEach((el) => {
      if (el?.id === item?.id) {
        const updatedData = {
          ...el,
          value: text
        };
        updatedPriorities = [...updatedPriorities, updatedData];
      } else {
        updatedPriorities = [...updatedPriorities, el];
      }
    });
    setPriorityData(updatedPriorities);
  };

  function handleFocusOut(item, text) {
    const trimmedValue = text.trim();
    if (!trimmedValue) return;
    updatePriority(item, trimmedValue);
  }

  if (error) return <div>error occured </div>;

  const onSave = () => {
    const columnName = DEFAULT_FIELDS_BY_COLUMN_NAME.PRIORITY;
    const baseData = data?.getField?.options || data?.getField?.fieldOptions;
    const updatedData = priorityData;
    const result = getParsedCrudData(baseData, updatedData);
    let updatedOptions = {};
    const { updatedItems } = result;
    if (updatedItems.length > 0) {
      updatedOptions = {
        ...updatedOptions,
        updated: updatedItems,
        updateOptions: updatedItems
      };
    }

    let variables = {
      id: data?.getField?.id,
      mandatoryOnClosure: mandatoryOnClosure,
      mandatoryOnCreate: mandatoryOnCreation
    };

    if (flags?.dependentField) {
      variables = {
        ...variables,
        updateOptions: map(updatedOptions?.updateOptions, (e) =>
          pick(e, ['id', 'value', 'order'])
        )
      };
    } else {
      variables = {
        ...variables,
        fieldOptionsInput: isEmpty(updatedOptions)
          ? undefined
          : JSON.stringify(updatedOptions)
      };
    }
    props.primaryAction(variables);
  };

  return (
    <PriorityWrapper className="full-height full-width">
      <div className="full-height full-width">
        <Page
          fullWidth
          variant="primary"
          contentHeight="140px"
          title={
            <Heading element="h1">
              <FormattedMessage id="admin.mandatoryFields.edit.default.Fields" />
            </Heading>
          }
          primaryAction={
            <ActionGroup
              primaryText={<FormattedMessage id="common.save" />}
              primaryLoading={props.saveLoading}
              primaryAction={onSave}
              secondaryAction={props.secondaryAction}
              primaryDisabled={!isEditAuthorised}
            />
          }
        >
          <TextStyle variation="strong" weight="Bold">
            <p className="heading">
              <FormattedMessage id="attribute.priority" />
            </p>
          </TextStyle>
          <TextStyle variation="subdued">
            <p className="description">
              <FormattedMessage id="admin.ImpactUrgencyPriority.priority.desc" />
            </p>
          </TextStyle>

          {loading ? (
            <>
              <Loader />
            </>
          ) : (
            <>
              <div className="mb10 priority-wrapper">
                {priorityData.map((item, index) => {
                  return (
                    <div key={item.id} className="flex g5 mb10">
                      <div>
                        <Priority
                          item={item}
                          handleFocusOut={handleFocusOut}
                          isEditAuthorised={isEditAuthorised}
                          index={index}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mb40">
                <PermissionToggle
                  hideShowToClients
                  mandatoryOnCreation={mandatoryOnCreation}
                  setMandatoryOnCreation={setMandatoryOnCreation}
                  mandatoryOnClosure={mandatoryOnClosure}
                  setMandatoryOnClosure={setMandatoryOnClosure}
                  disableEdit={!isEditAuthorised}
                  showWarning={props.showWarning}
                />
              </div>
            </>
          )}
        </Page>
      </div>
    </PriorityWrapper>
  );
}

export default PriorityPage;
